/**
 * Single source of truth for Webflow's asset-environment registry.
 *
 * Plain CommonJS so it can be `require`d from `config/*.js` (node-config
 * loads those files at startup) with no ts-node or compilation step.
 * The typed helper at ./getCurrentAssetEntry consumes the same data for
 * runtime (env, region) lookups, and the root config/default.js
 * re-exposes the same data as config.assetEnvRegistry for legacy
 * consumers (entrypoints/server/lib/fileUtils.ts) that still read the
 * registry via node-config.
 *
 * Adding a new entry:
 *  1. Append below, keyed by `<env>-<region>`.
 *  2. If the env is brand-new, also extend ENV_NAMES in
 *     ./getCurrentAssetEntry.ts and the `env` enum in
 *     config/schemas/asset-env-registry.schema.json.
 *  3. No per-lambda config seeding required -- the helper picks the
 *     entry up via process.env.APP_ENV / NODE_ENV / AWS_REGION.
 *
 * Runtime override: the ASSET_ENV_REGISTRY env var (JSON-encoded record)
 * overrides this baked-in data. Both the helper and root
 * config/default.js check that env var independently -- see those files
 * for the override-aware code paths. CORE-3359.
 */
'use strict';

const ASSET_ENV_REGISTRY = {
  'production-us-east-1': {
    env: 'production',
    region: 'us-east-1',
    s3bucket: 'webflow-prod-assets',
    siteCacheBucket: 'webflow-site-cache-production',
    cloudFlareCdn: 'cdn.prod.website-files.com',
    fileSslCdn: 'webflow-files-prod.global.ssl.fastly.net',
    // The CDN fields below are read through the registry via fileUtils.ts
    // CURRENT_* exports and are `required` in
    // config/schemas/asset-env-registry.schema.json.
    cdn: 'uploads.webflow.com',
    sslCdn: 'uploads-ssl.webflow.com',
    sslCdnWl: 'assets.website-files.com',
    globalSslCdn: 'global-uploads.webflow.com',
    globalSslCdnWl: 'assets-global.website-files.com',
    oldCdn: ['y7v4p6k4.ssl.hwcdn.net', 'daks2k3a4ib2z.cloudfront.net'],
  },
  'production-us-west-2': {
    env: 'production',
    region: 'us-west-2',
    s3bucket: 'webflow-prod-assets',
    siteCacheBucket: 'webflow-site-cache-production',
    cloudFlareCdn: 'cdn.prod.website-files.com',
    fileSslCdn: 'webflow-files-prod.global.ssl.fastly.net',
    cdn: 'uploads.webflow.com',
    sslCdn: 'uploads-ssl.webflow.com',
    sslCdnWl: 'assets.website-files.com',
    globalSslCdn: 'global-uploads.webflow.com',
    globalSslCdnWl: 'assets-global.website-files.com',
    oldCdn: ['y7v4p6k4.ssl.hwcdn.net', 'daks2k3a4ib2z.cloudfront.net'],
  },
  'acceptance-us-east-1': {
    env: 'acceptance',
    region: 'us-east-1',
    s3bucket: 'webflow-dev-assets',
    siteCacheBucket: 'webflow-site-cache-staging',
    cloudFlareCdn: 'cdn.dev.website-files.com',
    fileSslCdn: 'webflow-files-dev.global.ssl.fastly.net',
    cdn: 'cdn-dev-assets.webflow.com',
    sslCdn: 'dev-assets.website-files.com',
    sslCdnWl: 'dev-assets.website-files.com',
    globalSslCdn: 'dev-assets.website-files.com',
    globalSslCdnWl: 'dev-assets-global.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  'acceptance-us-west-2': {
    env: 'acceptance',
    region: 'us-west-2',
    s3bucket: 'webflow-dev-assets',
    siteCacheBucket: 'webflow-site-cache-staging',
    cloudFlareCdn: 'cdn.dev.website-files.com',
    fileSslCdn: 'webflow-files-dev.global.ssl.fastly.net',
    cdn: 'cdn-dev-assets.webflow.com',
    sslCdn: 'dev-assets.website-files.com',
    sslCdnWl: 'dev-assets.website-files.com',
    globalSslCdn: 'dev-assets.website-files.com',
    globalSslCdnWl: 'dev-assets-global.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  'dashboard-acceptance-us-east-1': {
    env: 'dashboard-acceptance',
    region: 'us-east-1',
    s3bucket: 'webflow-dev-assets',
    siteCacheBucket: 'webflow-site-cache-staging',
    cloudFlareCdn: 'cdn.dev.website-files.com',
    fileSslCdn: 'webflow-files-dev.global.ssl.fastly.net',
    cdn: 'cdn-dev-assets.webflow.com',
    sslCdn: 'dev-assets.website-files.com',
    sslCdnWl: 'dev-assets.website-files.com',
    globalSslCdn: 'dev-assets.website-files.com',
    globalSslCdnWl: 'dev-assets-global.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  'dashboard-acceptance-us-west-2': {
    env: 'dashboard-acceptance',
    region: 'us-west-2',
    s3bucket: 'webflow-dev-assets',
    siteCacheBucket: 'webflow-site-cache-staging',
    cloudFlareCdn: 'cdn.dev.website-files.com',
    fileSslCdn: 'webflow-files-dev.global.ssl.fastly.net',
    cdn: 'cdn-dev-assets.webflow.com',
    sslCdn: 'dev-assets.website-files.com',
    sslCdnWl: 'dev-assets.website-files.com',
    globalSslCdn: 'dev-assets.website-files.com',
    globalSslCdnWl: 'dev-assets-global.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  'development-us-east-1': {
    env: 'development',
    region: 'us-east-1',
    s3bucket: 'webflow-dev-assets',
    cloudFlareCdn: 'cdn.dev.website-files.com',
    fileSslCdn: 'webflow-files-dev.global.ssl.fastly.net',
    cdn: 'cdn-dev-assets.webflow.com',
    sslCdn: 'dev-assets.website-files.com',
    sslCdnWl: 'dev-assets.website-files.com',
    globalSslCdn: 'dev-assets.website-files.com',
    globalSslCdnWl: 'dev-assets-global.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  'sai-us-east-1': {
    env: 'sai',
    region: 'us-east-1',
    s3bucket: 'webflow-sai-assets',
    siteCacheBucket: 'webflow-site-cache-sai',
    cloudFlareCdn: 'cdn.sai.website-files.com',
    // sai's legacy fileUploads.fileSslCdn is '' -- mirror that here.
    // getSslAcceleratedUrl falls back to cloudFlareCdn for file-upload
    // assets when fileSslCdn is empty.
    fileSslCdn: '',
    cdn: 'cdn-assets.webflow-sai.com',
    sslCdn: 'uploads-ssl.webflow-sai.com',
    sslCdnWl: 'assets.sai.website-files.com',
    globalSslCdn: 'global-uploads.webflow-sai.com',
    globalSslCdnWl: 'assets-global.sai.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  'sai-us-west-2': {
    env: 'sai',
    region: 'us-west-2',
    s3bucket: 'webflow-sai-assets',
    siteCacheBucket: 'webflow-site-cache-sai',
    cloudFlareCdn: 'cdn.sai.website-files.com',
    fileSslCdn: '',
    cdn: 'cdn-assets.webflow-sai.com',
    sslCdn: 'uploads-ssl.webflow-sai.com',
    sslCdnWl: 'assets.sai.website-files.com',
    globalSslCdn: 'global-uploads.webflow-sai.com',
    globalSslCdnWl: 'assets-global.sai.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  // EU SAI runs the webflow-app pod in eu-west-1 against its own
  // regional assets bucket (provisioned by the infrastructure repo's
  // pulumi/projects/app-cdn-and-assets sai_legacy-production_eu-west-1
  // stack, fronted by the CloudFlare zone defined in
  // pulumi/projects/cloudflare-assets sai_eu.yaml). Unlike sai-us-east-1,
  // EU SAI does NOT replicate the legacy cdn-assets / uploads-ssl /
  // global-uploads.webflow-sai.com CloudFront distributions; everything
  // is served via the single CloudFlare-fronted host
  // cdn.sai.eu.website-files.com (s3 origin: 024376647576-eu-west-1-sai-
  // assets), so the branded cdn / sslCdn / globalSslCdn slots all point
  // at that host. The white-label hosts (assets / assets-global.sai.eu.
  // website-files.com) are the same CloudFlare-fronted bucket via
  // additional DNS records in the cloudflare-assets sai_eu stack.
  // Originally added as a production hotfix in PR #110690 (against
  // root config/default.js); mirrored into the helper here so that
  // post-#110619 the helper remains the single source of truth for
  // EU SAI's asset resolution.
  'sai-eu-west-1': {
    env: 'sai',
    region: 'eu-west-1',
    s3bucket: '024376647576-eu-west-1-sai-assets',
    siteCacheBucket: '024376647576-eu-west-1-sai-site-cache',
    cloudFlareCdn: 'cdn.sai.eu.website-files.com',
    // Mirror sai-us-east-1's empty fileSslCdn; getSslAcceleratedUrl
    // falls back to cloudFlareCdn for file-upload assets.
    fileSslCdn: '',
    cdn: 'cdn.sai.eu.website-files.com',
    sslCdn: 'cdn.sai.eu.website-files.com',
    sslCdnWl: 'assets.sai.eu.website-files.com',
    globalSslCdn: 'cdn.sai.eu.website-files.com',
    globalSslCdnWl: 'assets-global.sai.eu.website-files.com',
    oldCdn: [],
    // The bucket lives in eu-west-1, not the legacy global endpoint. HOST-913.
    // eslint-disable-next-line webflow/no-hardcoded-s3-endpoints -- registry data: source of truth for s3Host
    s3Host: 's3.eu-west-1.amazonaws.com',
  },
  // eu-central-1 has no assets infrastructure of its own. The react-renderer
  // / rss-renderer edge pods deployed there (HOST-3971) resolve assets from
  // the same single EU SAI bucket + CloudFlare-fronted host as eu-west-1, so
  // this entry mirrors sai-eu-west-1 verbatim and differs only in `region`.
  'sai-eu-central-1': {
    env: 'sai',
    region: 'eu-central-1',
    s3bucket: '024376647576-eu-west-1-sai-assets',
    siteCacheBucket: '024376647576-eu-west-1-sai-site-cache',
    cloudFlareCdn: 'cdn.sai.eu.website-files.com',
    fileSslCdn: '',
    cdn: 'cdn.sai.eu.website-files.com',
    sslCdn: 'cdn.sai.eu.website-files.com',
    sslCdnWl: 'assets.sai.eu.website-files.com',
    globalSslCdn: 'cdn.sai.eu.website-files.com',
    globalSslCdnWl: 'assets-global.sai.eu.website-files.com',
    oldCdn: [],
    // Same bucket as sai-eu-west-1 (lives in eu-west-1, not eu-central-1).
    // Not derived from `region` for exactly this reason. HOST-913.
    // eslint-disable-next-line webflow/no-hardcoded-s3-endpoints -- registry data: source of truth for s3Host
    s3Host: 's3.eu-west-1.amazonaws.com',
  },
  // EU production mirrors the EU SAI shape: a single regional assets bucket
  // (app-cdn-and-assets prod_legacy-production_eu-west-1) fronted by a
  // CloudFlare zone at prod.eu.website-files.com (same subdomain pattern as
  // sai.eu.website-files.com → cdn / assets / assets-global hostnames).
  // Unlike US production, there are no legacy uploads.webflow.com /
  // uploads-ssl.webflow.com CloudFront distributions in EU; every branded
  // CDN slot points at the CloudFlare-fronted host. Without this entry,
  // wf-app pods in eu-west-1 crash at module load in fileUtils.ts
  // ("No assetEnvRegistry entry for env=production region=eu-west-1").
  'production-eu-west-1': {
    env: 'production',
    region: 'eu-west-1',
    s3bucket: '024376647576-eu-west-1-prod-assets',
    siteCacheBucket: '024376647576-eu-west-1-prod-site-cache',
    cloudFlareCdn: 'cdn.prod.eu.website-files.com',
    fileSslCdn: '',
    cdn: 'cdn.prod.eu.website-files.com',
    sslCdn: 'cdn.prod.eu.website-files.com',
    sslCdnWl: 'assets.prod.eu.website-files.com',
    globalSslCdn: 'cdn.prod.eu.website-files.com',
    globalSslCdnWl: 'assets-global.prod.eu.website-files.com',
    oldCdn: [],
    // The bucket lives in eu-west-1, not the legacy global endpoint. HOST-913.
    // eslint-disable-next-line webflow/no-hardcoded-s3-endpoints -- registry data: source of truth for s3Host
    s3Host: 's3.eu-west-1.amazonaws.com',
  },
  // eu-central-1 has no assets infrastructure of its own. Secondary-region
  // pods resolve assets from the same EU prod bucket + CloudFlare hosts as
  // eu-west-1 (same pattern as sai-eu-central-1).
  'production-eu-central-1': {
    env: 'production',
    region: 'eu-central-1',
    s3bucket: '024376647576-eu-west-1-prod-assets',
    siteCacheBucket: '024376647576-eu-west-1-prod-site-cache',
    cloudFlareCdn: 'cdn.prod.eu.website-files.com',
    fileSslCdn: '',
    cdn: 'cdn.prod.eu.website-files.com',
    sslCdn: 'cdn.prod.eu.website-files.com',
    sslCdnWl: 'assets.prod.eu.website-files.com',
    globalSslCdn: 'cdn.prod.eu.website-files.com',
    globalSslCdnWl: 'assets-global.prod.eu.website-files.com',
    oldCdn: [],
    // Same bucket as production-eu-west-1 (lives in eu-west-1, not
    // eu-central-1). Not derived from `region` for exactly this reason.
    // HOST-913.
    // eslint-disable-next-line webflow/no-hardcoded-s3-endpoints -- registry data: source of truth for s3Host
    s3Host: 's3.eu-west-1.amazonaws.com',
  },
  // The `test` entry is baked in primarily so helper consumers exercised
  // under jest resolve cleanly. The jest base config
  // (packages/tooling/jest-configs/base.ts) forces NODE_ENV='test' and
  // leaves APP_ENV / ASSET_ENV_REGISTRY untouched, so without this entry
  // every getCurrentAssetEntry() call from a jest suite would throw
  // "no entry for env=\"test\" region=\"us-east-1\"" -- effectively
  // recreating the per-consumer config-seeding tax the inverted design
  // was meant to eliminate.
  //
  // Values are the literal defaults from config/test.js's
  // assetEnvRegistry. That file additionally honors CI env-var overrides
  // (WF_FILE_UPLOADS_BUCKET, WF_FILE_UPLOADS_SSL_CDN,
  // WF_CLOUDFLARE_SITE_ASSETS_CDN_URL, ...) so its signed-upload tests
  // can point at fake hosts. Those overrides flow through node-config
  // -> config.assetEnvRegistry -> entrypoints/server/lib/fileUtils.ts
  // ONLY -- they are intentionally not mirrored here, because a baked-in
  // record can't replay env-var-driven values at module load. Helper
  // consumers under jest see the literal defaults below.
  'test-us-east-1': {
    env: 'test',
    region: 'us-east-1',
    s3bucket: 'webflow-dev-assets',
    cloudFlareCdn: 'cdn.dev.website-files.com',
    fileSslCdn: 'webflow-files-dev.global.ssl.fastly.net',
    cdn: 'cdn-dev-assets.webflow.com',
    sslCdn: 'dev-assets.website-files.com',
    sslCdnWl: 'dev-assets.website-files.com',
    globalSslCdn: 'global-cdn-test.webflow.com',
    globalSslCdnWl: 'dev-assets-global.website-files.com',
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
  // dvm is a Buildkite-pipeline-only env (set by .buildkite/scripts/
  // set-node-env.sh when WF_BK_SELECTED_PIPELINE=dvm). The hostname
  // template `file-uploads-cdn.<dvm-instance>.us-east-1.wfdev.io` is
  // tied to the currently-active dvm instance (devm02 today); update
  // these values when the dvm instance changes.
  'dvm-us-east-1': {
    env: 'dvm',
    region: 'us-east-1',
    s3bucket: 'wf-file-uploads-devm02',
    cloudFlareCdn: 'file-uploads-cdn.devm02.us-east-1.wfdev.io',
    fileSslCdn: 'file-uploads-cdn.devm02.us-east-1.wfdev.io',
    cdn: 'file-uploads-cdn.devm02.us-east-1.wfdev.io',
    sslCdn: 'file-uploads-cdn.devm02.us-east-1.wfdev.io',
    sslCdnWl: 'file-uploads-cdn.devm02.us-east-1.wfdev.io',
    globalSslCdn: 'file-uploads-cdn.devm02.us-east-1.wfdev.io',
    globalSslCdnWl: 'file-uploads-cdn.devm02.us-east-1.wfdev.io',
    // Inherited from dev's hwcdn legacy host (the original dvm.js
    // fileUploads block carried a "// @TODO ???" comment on this
    // value). Cleanup tracked in DELVE-7374
    // (https://webflow.atlassian.net/browse/DELVE-7374) -- for now,
    // mirror today's behavior byte-for-byte.
    oldCdn: ['z9x9v2t5.ssl.hwcdn.net'],
  },
};

module.exports = {ASSET_ENV_REGISTRY};
